import { useState, useEffect, useRef } from "react"
import {
  Mail,
  Download,
  Briefcase,
  ExternalLink,
  Phone,
  Clock,
  Award,
  Loader2,
  MessageSquare,
  Sparkles,
  ChevronDown,
  NotebookPen,
  X,
} from "lucide-react"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/Page/AnnotationLayer.css"
import "react-pdf/dist/Page/TextLayer.css"
import linkedin from "../../assets/icons/linkedin.png"
import github from "../../assets/icons/github.png"
import leetcode from "../../assets/icons/leetcode.png"
import internet from "../../assets/icons/internet.png"
import placeholder from "../../assets/placeholder.png"

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs"

const BASE_URL = "https://hire100x-e1dd4.uc.r.appspot.com"

const proxyUrl = (url) =>
  url
    ? `${BASE_URL}/upload-resume/proxy-resume?url=${encodeURIComponent(url)}`
    : null

const PIPELINE_STAGES = [
  { label: "NEW APPLICANT", value: "pending" },
  { label: "RECRUITER SCREEN", value: "recruiter-screen" },
  { label: "INTERVIEW", value: "interview" },
  { label: "OFFER", value: "offer" },
  { label: "BACKGROUND CHECK", value: "background-check" },
  { label: "HIRED", value: "hired" },
]

const statusToStage = (status) =>
  PIPELINE_STAGES.find((s) => s.value === status) ?? PIPELINE_STAGES[0]

const InternalNotesModal = ({ notes, onSave, onClose, isSaving }) => {
  const [draft, setDraft] = useState(notes || "")
  const textareaRef = useRef(null)

  useEffect(() => { textareaRef.current?.focus() }, [])

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.32)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 60, backdropFilter: "blur(1px)",
    }}>
      <div style={{
        background: "#fff",
        borderRadius: 10,
        border: "0.5px solid #e5e7eb",
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        width: "100%", maxWidth: 480, margin: "0 16px",
        display: "flex", flexDirection: "column",
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}>

        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "13px 16px",
          borderBottom: "0.5px solid #e5e7eb",
          background: "#fafaf9",
          borderRadius: "10px 10px 0 0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#111827", flexShrink: 0 }} />
            <span style={{
              fontSize: 12, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.07em",
              color: "#6b7280",
              fontFamily: "'IBM Plex Mono', monospace",
            }}>
              Internal Notes
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 26, height: 26,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "#fff", border: "0.5px solid #e5e7eb",
              borderRadius: 6, cursor: "pointer", color: "#9ca3af",
              outline: "none", transition: "all 0.12s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#111827"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#111827" }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#9ca3af"; e.currentTarget.style.borderColor = "#e5e7eb" }}
          >
            <X size={13} strokeWidth={2} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 16px 20px" }}>
          <p style={{
            fontSize: 11, fontWeight: 600,
            textTransform: "uppercase", letterSpacing: "0.07em",
            color: "#9ca3af", marginBottom: 10,
            fontFamily: "'IBM Plex Mono', monospace",
          }}>
            Private · visible to your team only
          </p>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add notes about this candidate…"
            rows={6}
            style={{
              width: "100%", padding: "10px 12px",
              border: "0.5px solid #e5e7eb",
              borderRadius: 7,
              fontSize: 13, fontWeight: 400,
              color: "#111827", lineHeight: 1.65,
              fontFamily: "'IBM Plex Sans', sans-serif",
              resize: "vertical", outline: "none",
              background: "#fafaf9",
              boxSizing: "border-box",
              transition: "border-color 0.12s",
            }}
            onFocus={(e) => { e.target.style.borderColor = "#9ca3af" }}
            onBlur={(e)  => { e.target.style.borderColor = "#e5e7eb" }}
          />
        </div>

        {/* Footer */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          gap: 8, padding: "12px 16px",
          borderTop: "0.5px solid #e5e7eb",
        }}>
          <button
            onClick={onClose}
            style={{
              height: 32, padding: "0 14px",
              border: "0.5px solid #e5e7eb", borderRadius: 7,
              background: "#fff", color: "#374151",
              fontSize: 12, fontWeight: 600,
              fontFamily: "'IBM Plex Sans', sans-serif",
              cursor: "pointer", outline: "none",
              transition: "border-color 0.12s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af" }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb" }}
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(draft)}
            disabled={isSaving}
            style={{
              height: 32, padding: "0 14px",
              border: "none", borderRadius: 7,
              background: isSaving ? "#9ca3af" : "#111827",
              color: "#fff",
              fontSize: 12, fontWeight: 600,
              fontFamily: "'IBM Plex Sans', sans-serif",
              cursor: isSaving ? "not-allowed" : "pointer",
              outline: "none",
              display: "flex", alignItems: "center", gap: 6,
              transition: "background 0.12s",
            }}
            onMouseEnter={(e) => { if (!isSaving) e.currentTarget.style.background = "#27272a" }}
            onMouseLeave={(e) => { if (!isSaving) e.currentTarget.style.background = "#111827" }}
          >
            {isSaving && <Loader2 size={11} strokeWidth={2} className="animate-spin" />}
            {isSaving ? "Saving…" : "Save notes"}
          </button>
        </div>

      </div>
    </div>
  )
}

const CandidateDetailsContent = ({
  orgId,
  jobId,
  jobListing,
  selectedCandidate,
  rejectingCandidateId,
  onRejectCandidate,
  onScheduleInterview,
  onMessageCandidate,
  onStageChange, // optional: (applicantId, newStage) => void — lets parent update its list
}) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showAllSkills, setShowAllSkills] = useState(false)
  const [profileTab, setProfileTab] = useState("parsed")
  const [numPages, setNumPages] = useState(null)
  const [containerWidth, setContainerWidth] = useState(700)
  const [stageDropdownOpen, setStageDropdownOpen] = useState(false)
  const [currentStage, setCurrentStage] = useState(PIPELINE_STAGES[0])
  const [stageUpdating, setStageUpdating] = useState(false)
    const [showNotesModal, setShowNotesModal] = useState(false)
    const [internalNotes, setInternalNotes] = useState(
      selectedCandidate?.internalNotes || "",
    )
    const [isSavingNotes, setIsSavingNotes] = useState(false)
  const pdfContainerRef = useRef(null)

  const isRejecting = rejectingCandidateId === selectedCandidate?.applicantId
  const isRejected = selectedCandidate?.status === "rejected"
  const resumeUrl = proxyUrl(selectedCandidate?.resume?.resumeUrl)
  const pdfWidth = Math.min(containerWidth - 48, 860)

  // ── Populate stage from DB status on candidate change ──────────────────────
  useEffect(() => {
    setCurrentStage(statusToStage(selectedCandidate?.applicationStatus))
    setInternalNotes(selectedCandidate?.internalNotes || "")
  }, [
    selectedCandidate?.applicantId,
    selectedCandidate?.applicationStatus,
    selectedCandidate?.internalNotes,
  ])

  // ── Fetch user profile ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchUserData = async () => {
      if (!selectedCandidate?.applicantId) {
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        const response = await fetch(
          `${BASE_URL}/users/${selectedCandidate.applicantId}`,
        )
        if (!response.ok) throw new Error("Failed to fetch user data")
        const data = await response.json()
        if (data.success && data.data) setUser(data.data)
      } catch (err) {
        console.error("Error fetching user data:", err)
      } finally {
        setLoading(false)
      }
    }
    fetchUserData()
  }, [selectedCandidate?.applicantId])

  // ── PDF container resize observer ──────────────────────────────────────────
  useEffect(() => {
    if (!pdfContainerRef.current) return
    const ro = new ResizeObserver(([entry]) =>
      setContainerWidth(entry.contentRect.width),
    )
    ro.observe(pdfContainerRef.current)
    return () => ro.disconnect()
  }, [])
  
  // ── Stage update handler ────────────────────────────────────────────────────
  const handleStageSelect = async (stage) => {
    if (stage.value === currentStage.value) {
      setStageDropdownOpen(false)
      return
    }

    const prevStage = currentStage
    setCurrentStage(stage)
    setStageDropdownOpen(false)
    setStageUpdating(true)

    try {
      const res = await fetch(
        `${BASE_URL}/jobListings/${orgId}/${jobId}/candidates/${selectedCandidate.applicantId}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type: "applicant", status: stage.value }),
        },
      )
      if (!res.ok) throw new Error("Status update failed")
      // Notify parent so the candidate list can update its local state
      onStageChange?.(selectedCandidate.applicantId, stage.value)
    } catch (err) {
      console.error("Failed to update stage:", err)
      // Roll back optimistic update
      setCurrentStage(prevStage)
    } finally {
      setStageUpdating(false)
    }
  }

   const handleSaveNotes = async (notes) => {
     setIsSavingNotes(true)
     try {
       await fetch(
         `${BASE_URL}/jobListings/${orgId}/${jobId}/candidates/${selectedCandidate.applicantId}/note`,
         {
           method: "PATCH",
           headers: { "Content-Type": "application/json" },
           body: JSON.stringify({ internalNotes: notes }),
         },
       )
       setInternalNotes(notes)
       setShowNotesModal(false)
     } catch (err) {
       console.error("Failed to save notes:", err)
     } finally {
       setIsSavingNotes(false)
     }
   }

  const screeningQuestions = selectedCandidate?.screeningQuestions || []

  const scoreColor = (score) => {
    if (score === 0)
      return { badge: "bg-red-50 text-red-600", bar: "bg-red-400" }
    if (score <= 50)
      return { badge: "bg-orange-50 text-orange-600", bar: "bg-orange-400" }
    if (score <= 75)
      return { badge: "bg-yellow-50 text-yellow-700", bar: "bg-yellow-400" }
    return { badge: "bg-green-50 text-green-700", bar: "bg-green-500" }
  }

  if (!selectedCandidate) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
        <div
          className="w-10 h-10 border border-zinc-200 bg-zinc-50 flex items-center justify-center mb-3"
          style={{ borderRadius: 0 }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#a1a1aa"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
          </svg>
        </div>
        <p
          className="text-[13px] font-bold uppercase tracking-widest text-zinc-400 mb-1"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          No matching application found
        </p>
        <p
          className="text-[13px] text-zinc-500"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          Likely a manual interview not linked to an applicant
        </p>
      </div>
    )
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        .hide-scroll::-webkit-scrollbar { display: none; }
        .hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        .profile-tab-btn {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 11px; font-weight: 500;
          letter-spacing: 0.06em; text-transform: uppercase;
          padding: 5px 14px;
          border: none; background: none;
          color: #9ca3af; cursor: pointer;
          border-radius: 6px;
          transition: all 0.12s;
        }
        .profile-tab-btn:hover { color: #374151; background: #f3f4f6; }
        .profile-tab-btn.active { background: #111827; color: #fff; }
      `}</style>

      {/* ── Action Bar ── */}
      <div
        className="bg-white border-b border-zinc-200 px-6 py-3"
        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {selectedCandidate.matchingScore && (
              <>
                <div className="flex items-center gap-2">
                  <span
                    className="text-[11px] font-bold uppercase tracking-widest text-zinc-500"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  >
                    Overall Match
                  </span>
                  <span
                    className="text-sm font-bold text-zinc-900"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  >
                    {selectedCandidate.matchingScore.finalScore || 0}%
                  </span>
                </div>
                <div className="h-4 w-px bg-zinc-200" />
                <span
                  className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 border border-zinc-200 text-zinc-500 bg-zinc-50"
                  style={{
                    borderRadius: 0,
                    fontFamily: "'IBM Plex Mono', monospace",
                  }}
                >
                  {selectedCandidate.matchingScore.matchLevel || "NO_MATCH"}
                </span>
              </>
            )}
          </div>
<div
            style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}
          >
            {/* Message */}
            <button
              onClick={() => {
                const context = {
                  candidate: {
                    firstName: selectedCandidate?.applicantName?.split(" ")[0] ?? null,
                    fullName: selectedCandidate?.applicantName ?? null,
                  },
                  job: {
                    role: jobListing?.role ?? null,
                    company: jobListing?.organizationName ?? null,
                  },
                }
                onMessageCandidate?.(selectedCandidate, context)
              }}
              disabled={isRejected}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                height: 32, padding: "0 12px",
                borderRadius: 7, border: "0.5px solid #e5e7eb",
                background: isRejected ? "#f9fafb" : "#fff",
                color: isRejected ? "#d1d5db" : "#374151",
                fontSize: 12, fontWeight: 600,
                fontFamily: "'IBM Plex Sans', sans-serif",
                cursor: isRejected ? "not-allowed" : "pointer",
                transition: "border-color 0.12s, background 0.12s",
              }}
              onMouseEnter={(e) => { if (!isRejected) { e.currentTarget.style.borderColor = "#d1d5db"; e.currentTarget.style.background = "#f9fafb" } }}
              onMouseLeave={(e) => { if (!isRejected) { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.background = "#fff" } }}
            >
              <Mail size={12} strokeWidth={2} />
              Message
            </button>

            {/* Schedule */}
            <button
              onClick={() => onScheduleInterview(selectedCandidate)}
              disabled={isRejected}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                height: 32, padding: "0 12px",
                borderRadius: 7, border: "none",
                background: isRejected ? "#e5e7eb" : "#111827",
                color: isRejected ? "#9ca3af" : "#fff",
                fontSize: 12, fontWeight: 600,
                fontFamily: "'IBM Plex Sans', sans-serif",
                cursor: isRejected ? "not-allowed" : "pointer",
                transition: "background 0.12s",
              }}
              onMouseEnter={(e) => { if (!isRejected) e.currentTarget.style.background = "#27272a" }}
              onMouseLeave={(e) => { if (!isRejected) e.currentTarget.style.background = "#111827" }}
            >
              <Clock size={12} strokeWidth={2} />
              Schedule
            </button>

            {/* Archive */}
            <button
              onClick={() => onRejectCandidate(selectedCandidate)}
              disabled={isRejecting || isRejected || rejectingCandidateId !== null}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                height: 32, padding: "0 12px",
                borderRadius: 7,
                border: isRejected
                  ? "0.5px solid #fecaca"
                  : "0.5px solid #e5e7eb",
                background: isRejected
                  ? "#fef2f2"
                  : isRejecting
                    ? "#fef2f2"
                    : rejectingCandidateId !== null
                      ? "#f9fafb"
                      : "#fff",
                color: isRejected || isRejecting
                  ? "#f87171"
                  : rejectingCandidateId !== null
                    ? "#d1d5db"
                    : "#374151",
                fontSize: 12, fontWeight: 600,
                fontFamily: "'IBM Plex Sans', sans-serif",
                cursor: isRejecting || isRejected || rejectingCandidateId !== null ? "not-allowed" : "pointer",
                opacity: isRejected ? 0.7 : 1,
                transition: "border-color 0.12s, background 0.12s",
              }}
              onMouseEnter={(e) => {
                if (!isRejecting && !isRejected && rejectingCandidateId === null) {
                  e.currentTarget.style.borderColor = "#fecaca"
                  e.currentTarget.style.background = "#fef2f2"
                  e.currentTarget.style.color = "#f87171"
                }
              }}
              onMouseLeave={(e) => {
                if (!isRejecting && !isRejected && rejectingCandidateId === null) {
                  e.currentTarget.style.borderColor = "#e5e7eb"
                  e.currentTarget.style.background = "#fff"
                  e.currentTarget.style.color = "#374151"
                }
              }}
            >
              {isRejecting ? (
                <Loader2 size={12} strokeWidth={2} className="animate-spin" />
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="21 8 21 21 3 21 3 8" />
                  <rect x="1" y="3" width="22" height="5" />
                  <line x1="10" y1="12" x2="14" y2="12" />
                </svg>
              )}
              {isRejected ? "Archived" : isRejecting ? "Archiving…" : "Archive"}
            </button>

            {/* Divider */}
            <div style={{ width: 1, height: 20, background: "#e5e7eb", flexShrink: 0 }} />

            {/* Pipeline stage dropdown trigger */}
            <button
              onClick={() => setStageDropdownOpen(!stageDropdownOpen)}
              disabled={stageUpdating}
              style={{
                display: "inline-flex", flexDirection: "column",
                alignItems: "stretch", gap: 5,
                padding: "5px 10px", height: 32,
                borderRadius: 7, border: "0.5px solid #e5e7eb",
                background: "#fff",
                cursor: stageUpdating ? "not-allowed" : "pointer",
                minWidth: 148,
                opacity: stageUpdating ? 0.6 : 1,
                transition: "border-color 0.12s, opacity 0.15s",
              }}
              onMouseEnter={(e) => { if (!stageUpdating) e.currentTarget.style.borderColor = "#d1d5db" }}
              onMouseLeave={(e) => { if (!stageUpdating) e.currentTarget.style.borderColor = "#e5e7eb" }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
                  {stageUpdating && (
                    <Loader2 size={10} className="animate-spin" style={{ color: "#9ca3af", flexShrink: 0 }} />
                  )}
                  <span style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11, fontWeight: 600,
                    letterSpacing: "0.05em", textTransform: "uppercase",
                    color: "#111827",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {currentStage.label.length > 13
                      ? currentStage.label.slice(0, 13) + "…"
                      : currentStage.label}
                  </span>
                </div>
                <ChevronDown size={11} style={{ color: "#9ca3af", flexShrink: 0 }} />
              </div>
              {/* Progress bar */}
              <div style={{ display: "flex", gap: 2 }}>
                {PIPELINE_STAGES.map((stage, i) => (
                  <div
                    key={i}
                    style={{
                      flex: 1, height: 2, borderRadius: 1,
                      background: i <= PIPELINE_STAGES.findIndex((s) => s.value === currentStage.value)
                        ? "#2563eb" : "#e5e7eb",
                      transition: "background 0.2s",
                    }}
                  />
                ))}
              </div>
            </button>

            {/* Dropdown panel */}
            {stageDropdownOpen && (
              <>
                <div
                  style={{ position: "fixed", inset: 0, zIndex: 40 }}
                  onClick={() => setStageDropdownOpen(false)}
                />
                <div
                  style={{
                    position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 50,
                    width: 210, overflow: "hidden",
                    background: "#1e2535",
                    borderRadius: 10,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                  }}
                >
                  {PIPELINE_STAGES.map((stage) => {
                    const active = stage.value === currentStage.value
                    return (
                      <button
                        key={stage.value}
                        onClick={() => handleStageSelect(stage)}
                        style={{
                          width: "100%", display: "flex", alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 14px",
                          background: active ? "#111827" : "transparent",
                          border: "none",
                          borderBottom: "1px solid rgba(255,255,255,0.05)",
                          cursor: "pointer",
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 11, fontWeight: 700,
                          letterSpacing: "0.06em", textTransform: "uppercase",
                          color: active ? "#fff" : "#8b95aa",
                          textAlign: "left",
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "#252e40" }}
                        onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent" }}
                      >
                        {stage.label}
                        {active && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                        {stage.value === "hired" && !active && (
                          <span style={{ fontSize: 13 }}>👍</span>
                        )}
                      </button>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Three-Column Body ── */}
      <div
        className="flex-1 flex overflow-hidden"
        style={{
          fontFamily: "'IBM Plex Sans', sans-serif",
          background: "#f5f5f5",
          gap: 0,
        }}
      >
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-6 h-6 text-zinc-400 animate-spin mx-auto mb-2" />
              <p
                className="text-xs text-zinc-400"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                Loading profile…
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* ── Column 1: Candidate Profile ── */}
            <div
              ref={pdfContainerRef}
              className="flex-1 overflow-y-auto hide-scroll"
              style={{ background: "#f5f5f5" }}
            >
              <div style={{ padding: "20px 20px 40px" }}>
                {/* ── Profile Header Card ── */}
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 0,
                    padding: "24px",
                    marginBottom: 12,
                  }}
                >
                  <div className="flex items-start gap-5">
                    <img
                      src={user?.profileImageUrl || placeholder}
                      alt={user?.name || selectedCandidate.applicantName}
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: "50%",
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h1
                        style={{
                          fontSize: 20,
                          fontWeight: 600,
                          color: "#111827",
                          marginBottom: 3,
                          lineHeight: 1.3,
                        }}
                      >
                        {user?.name || selectedCandidate.applicantName}
                      </h1>
                      <p
                        style={{
                          fontSize: 14,
                          color: "#6b7280",
                          marginBottom: 10,
                        }}
                      >
                        {user?.personal_information?.currentJobTitle ||
                          selectedCandidate.personal_information
                            ?.currentJobTitle ||
                          "Position not specified"}
                        {(user?.personal_information?.currentCompany ||
                          selectedCandidate.personal_information
                            ?.currentCompany) && (
                          <>
                            <span style={{ color: "#d1d5db", margin: "0 5px" }}>
                              at
                            </span>
                            <span style={{ color: "#111827" }}>
                              {user?.personal_information?.currentCompany ||
                                selectedCandidate.personal_information
                                  ?.currentCompany}
                            </span>
                          </>
                        )}
                      </p>

                      {/* Meta badges */}
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                          marginBottom: 12,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "4px 10px",
                            background: "#f3f4f6",
                            borderRadius: 6,
                          }}
                        >
                          <Briefcase size={12} style={{ color: "#6b7280" }} />
                          <span style={{ fontSize: 12, color: "#6b7280" }}>
                            {user?.personal_information?.yearsExperience ||
                              selectedCandidate.personal_information
                                ?.yearsExperience ||
                              "N/A"}{" "}
                            years
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "4px 10px",
                            background: "#eff6ff",
                            borderRadius: 6,
                          }}
                        >
                          <Clock size={12} style={{ color: "#2563eb" }} />
                          <span style={{ fontSize: 12, color: "#1d4ed8" }}>
                            {user?.job_preferences?.noticePeriod ||
                              selectedCandidate.job_preferences?.noticePeriod ||
                              "Not specified"}{" "}
                            notice
                          </span>
                        </div>
                      </div>

                      {/* Phone */}
                      {(user?.phone || selectedCandidate.phone) && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            marginBottom: 12,
                          }}
                        >
                          <Phone size={13} style={{ color: "#9ca3af" }} />
                          <span style={{ fontSize: 13, color: "#6b7280" }}>
                            {user?.phone || selectedCandidate.phone}
                          </span>
                        </div>
                      )}

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        {/* Left — social links */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {[
                            { icon: linkedin, alt: "LinkedIn",  link: user?.social_links?.linkedin  },
                            { icon: github,   alt: "GitHub",    link: user?.social_links?.github    },
                            { icon: leetcode, alt: "LeetCode",  link: user?.social_links?.leetcode  },
                            { icon: internet, alt: "Portfolio", link: user?.social_links?.portfolio },
                          ].map((social, index) => (
                            <button
                              key={index}
                              onClick={() => social.link && window.open(social.link, "_blank")}
                              title={social.alt}
                              disabled={!social.link}
                              style={{
                                padding: 7,
                                borderRadius: 8,
                                background: "#f9fafb",
                                border: "1px solid #e5e7eb",
                                cursor: social.link ? "pointer" : "not-allowed",
                                opacity: social.link ? 1 : 0.4,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <img
                                src={social.icon}
                                alt={social.alt}
                                style={{ width: 15, height: 15, objectFit: "contain" }}
                              />
                            </button>
                          ))}
                        </div>

                        {/* Right — Download + Internal Notes */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {selectedCandidate.resume && (
                            <button
                              onClick={() => {
                                const link = document.createElement("a")
                                link.href = selectedCandidate.resume.resumeUrl
                                link.download = selectedCandidate.resume.fileName
                                link.click()
                              }}
                              title="Download resume"
                              style={{
                                padding: 7,
                                borderRadius: 8,
                                background: "#f9fafb",
                                border: "1px solid #e5e7eb",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#6b7280",
                              }}
                            >
                              <Download size={15} />
                            </button>
                          )}

                             {!internalNotes && (
                          <button
                            onClick={() => setShowNotesModal(true)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                              height: 30,
                              padding: "0 10px",
                              borderRadius: 8,
                              border: "1px dashed #d1d5db",
                              background: "transparent",
                              color: "#6b7280",
                              fontSize: 12,
                              fontWeight: 600,
                              fontFamily: "'IBM Plex Sans', sans-serif",
                              cursor: "pointer",
                              transition: "all 0.12s",
                              whiteSpace: "nowrap",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = "#9ca3af"
                              e.currentTarget.style.color = "#374151"
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = "#d1d5db"
                              e.currentTarget.style.color = "#6b7280"
                            }}
                          >
                            <NotebookPen size={12} />
                            Internal Notes
                          </button>
                        )}
 
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                          {/* ── Internal Notes display ── */}
{/* ── Internal Notes display ── */}
                {internalNotes && (
                  <div style={{
                    background: "#fff",
                    borderRadius: 0,
                    padding: "22px 24px",
                    marginBottom: 12,
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 10,
                    }}>
                      <h2 style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: "#111827",
                        margin: 0,
                      }}>
                        Internal Notes
                      </h2>
                      <button
                        onClick={() => setShowNotesModal(true)}
                        style={{
                          height: 28, padding: "0 10px",
                          border: "0.5px solid #e5e7eb", borderRadius: 7,
                          background: "#fff", color: "#6b7280",
                          fontSize: 12, fontWeight: 600,
                          fontFamily: "'IBM Plex Sans', sans-serif",
                          cursor: "pointer", outline: "none",
                          transition: "border-color 0.12s",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af" }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb" }}
                      >
                        Edit
                      </button>
                    </div>
                    <p style={{
                      fontSize: 14,
                      color: "#4b5563",
                      lineHeight: 1.65,
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      whiteSpace: "pre-wrap",
                      margin: 0,
                    }}>
                      {internalNotes}
                    </p>
                  </div>
                )}

                {/* ── Parsed / Original Tab Toggle ── */}
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 0,
                    padding: "10px 16px",
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <button
                    className={`profile-tab-btn${profileTab === "parsed" ? " active" : ""}`}
                    onClick={() => setProfileTab("parsed")}
                  >
                    Parsed
                  </button>
                  <button
                    className={`profile-tab-btn${profileTab === "original" ? " active" : ""}`}
                    onClick={() => setProfileTab("original")}
                  >
                    Original
                  </button>
                </div>

                {/* ── ORIGINAL: PDF Resume ── */}
                {profileTab === "original" && (
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: 0,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "16px 20px 32px",
                        background: "#fafafa",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      {resumeUrl ? (
                        <Document
                          file={resumeUrl}
                          onLoadSuccess={({ numPages }) =>
                            setNumPages(numPages)
                          }
                          loading={
                            <div
                              style={{
                                width: pdfWidth,
                                aspectRatio: "1/1.414",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 10,
                              }}
                            >
                              <Loader2
                                size={16}
                                style={{ color: "#a1a1aa" }}
                                className="animate-spin"
                              />
                              <p
                                style={{
                                  color: "#a1a1aa",
                                  fontSize: 11,
                                  fontFamily: "'IBM Plex Mono',monospace",
                                  letterSpacing: "0.1em",
                                  textTransform: "uppercase",
                                  margin: 0,
                                }}
                              >
                                Loading Resume
                              </p>
                            </div>
                          }
                          error={
                            <div
                              style={{
                                width: pdfWidth,
                                aspectRatio: "1/1.414",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <p
                                style={{
                                  color: "#a1a1aa",
                                  fontSize: 11,
                                  fontFamily: "'IBM Plex Mono',monospace",
                                  letterSpacing: "0.1em",
                                  textTransform: "uppercase",
                                  margin: 0,
                                }}
                              >
                                Could not load resume
                              </p>
                            </div>
                          }
                        >
                          {Array.from({ length: numPages || 0 }, (_, i) => (
                            <div
                              key={i}
                              style={{
                                marginBottom: i < (numPages || 1) - 1 ? 12 : 0,
                                border: "1px solid #e8e8e8",
                                borderRadius: 4,
                                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                                lineHeight: 0,
                                background: "#fff",
                              }}
                            >
                              <Page
                                pageNumber={i + 1}
                                width={pdfWidth}
                                renderAnnotationLayer={false}
                                renderTextLayer={false}
                              />
                            </div>
                          ))}
                        </Document>
                      ) : (
                        <div
                          style={{
                            width: pdfWidth,
                            aspectRatio: "1/1.414",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <p
                            style={{
                              color: "#a1a1aa",
                              fontSize: 11,
                              fontFamily: "'IBM Plex Mono',monospace",
                              letterSpacing: "0.1em",
                              textTransform: "uppercase",
                              margin: 0,
                            }}
                          >
                            No resume attached
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── PARSED: Profile sections ── */}
                {profileTab === "parsed" && (
                  <>
                    {/* Work Experience */}
                    {user?.work_history && user.work_history.length > 0 && (
                      <div
                        style={{
                          background: "#fff",
                          borderRadius: 0,
                          padding: "22px 24px",
                          marginBottom: 12,
                        }}
                      >
                        <h2
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: "#111827",
                            marginBottom: 18,
                          }}
                        >
                          Work Experience
                        </h2>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 22,
                          }}
                        >
                          {user.work_history.map((work, index) => (
                            <div
                              key={index}
                              style={{ display: "flex", gap: 12 }}
                            >
                              <img
                                src={placeholder}
                                alt={work.companyName || "Company"}
                                style={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: 8,
                                  objectFit: "cover",
                                  flexShrink: 0,
                                  border: "1px solid #f3f4f6",
                                }}
                              />
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    justifyContent: "space-between",
                                    marginBottom: 2,
                                  }}
                                >
                                  <div>
                                    <p
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: "#111827",
                                      }}
                                    >
                                      {work.role || "Position"}
                                    </p>
                                    <p
                                      style={{ fontSize: 13, color: "#6b7280" }}
                                    >
                                      {work.companyName || "Company"}
                                    </p>
                                  </div>
                                  <p
                                    style={{
                                      fontSize: 12,
                                      color: "#9ca3af",
                                      flexShrink: 0,
                                      marginLeft: 12,
                                    }}
                                  >
                                    {work.startMonth && work.startYear
                                      ? `${work.startMonth} ${work.startYear} – ${work.isCurrentJob || (!work.endMonth && !work.endYear) ? "Present" : `${work.endMonth} ${work.endYear}`}`
                                      : "Present"}
                                  </p>
                                </div>
                                {work.description && (
                                  <p
                                    style={{
                                      fontSize: 14,
                                      color: "#4b5563",
                                      lineHeight: 1.65,
                                      marginTop: 6,
                                      whiteSpace: "pre-line",
                                    }}
                                  >
                                    {work.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Education */}
                    {user?.education && user.education.length > 0 && (
                      <div
                        style={{
                          background: "#fff",
                          borderRadius: 0,
                          padding: "22px 24px",
                          marginBottom: 12,
                        }}
                      >
                        <h2
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: "#111827",
                            marginBottom: 18,
                          }}
                        >
                          Education
                        </h2>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 16,
                          }}
                        >
                          {user.education.map((entry, index) => (
                            <div
                              key={index}
                              style={{ display: "flex", gap: 12 }}
                            >
                              <img
                                src={placeholder}
                                alt={entry.institute || "School"}
                                style={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: 8,
                                  objectFit: "cover",
                                  flexShrink: 0,
                                  border: "1px solid #f3f4f6",
                                }}
                              />
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <p
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 600,
                                      color: "#111827",
                                    }}
                                  >
                                    {entry.degree}
                                    {entry.field && (
                                      <span
                                        style={{
                                          fontWeight: 400,
                                          color: "#6b7280",
                                        }}
                                      >
                                        {", "}
                                        {entry.field}
                                      </span>
                                    )}
                                  </p>
                                  <p
                                    style={{
                                      fontSize: 12,
                                      color: "#9ca3af",
                                      flexShrink: 0,
                                      marginLeft: 12,
                                    }}
                                  >
                                    {entry.startMonth &&
                                    entry.startYear &&
                                    entry.endMonth &&
                                    entry.endYear
                                      ? `${entry.startMonth} ${entry.startYear} – ${entry.endMonth} ${entry.endYear}`
                                      : entry.graduationYear || ""}
                                  </p>
                                </div>
                                <p
                                  style={{
                                    fontSize: 13,
                                    color: "#6b7280",
                                    marginTop: 2,
                                  }}
                                >
                                  {entry.institute}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Projects */}
                    {user?.projects && user.projects.length > 0 && (
                      <div
                        style={{
                          background: "#fff",
                          borderRadius: 0,
                          padding: "22px 24px",
                          marginBottom: 12,
                        }}
                      >
                        <h2
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: "#111827",
                            marginBottom: 18,
                          }}
                        >
                          Projects
                        </h2>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 18,
                          }}
                        >
                          {user.projects.map((project, index) => (
                            <div key={index}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  justifyContent: "space-between",
                                  marginBottom: 4,
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: "#374151",
                                  }}
                                >
                                  {project.title || "Project Title"}
                                </p>
                                {project.url && (
                                  <button
                                    onClick={() =>
                                      window.open(
                                        project.url,
                                        "_blank",
                                        "noopener,noreferrer",
                                      )
                                    }
                                    style={{
                                      color: "#9ca3af",
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                      flexShrink: 0,
                                      display: "flex",
                                    }}
                                  >
                                    <ExternalLink size={15} />
                                  </button>
                                )}
                              </div>
                              <p
                                style={{
                                  fontSize: 14,
                                  color: "#4b5563",
                                  lineHeight: 1.65,
                                  marginBottom: 4,
                                }}
                              >
                                {project.description || "Project description"}
                              </p>
                              {project.url && (
                                <button
                                  onClick={() =>
                                    window.open(
                                      project.url,
                                      "_blank",
                                      "noopener,noreferrer",
                                    )
                                  }
                                  style={{
                                    fontSize: 13,
                                    color: "#2563eb",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                  }}
                                >
                                  {project.url}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Skills */}
                    {user?.resumeData?.skills &&
                      user.resumeData.skills.length > 0 && (
                        <div
                          style={{
                            background: "#fff",
                            borderRadius: 0,
                            padding: "22px 24px",
                            marginBottom: 12,
                          }}
                        >
                          <h2
                            style={{
                              fontSize: 15,
                              fontWeight: 600,
                              color: "#111827",
                              marginBottom: 14,
                            }}
                          >
                            Skills
                          </h2>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 6,
                            }}
                          >
                            {(showAllSkills
                              ? user.resumeData.skills
                              : user.resumeData.skills.slice(0, 15)
                            ).map((skill, index) => (
                              <span
                                key={index}
                                style={{
                                  padding: "5px 12px",
                                  background: "#f3f4f6",
                                  color: "#374151",
                                  fontSize: 13,
                                  borderRadius: 100,
                                }}
                              >
                                {skill}
                              </span>
                            ))}
                            {user.resumeData.skills.length > 15 && (
                              <button
                                onClick={() => setShowAllSkills(!showAllSkills)}
                                style={{
                                  padding: "5px 12px",
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: "#2563eb",
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  borderRadius: 100,
                                }}
                              >
                                {showAllSkills
                                  ? "Show less"
                                  : `+${user.resumeData.skills.length - 15} more`}
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                    {/* Achievements */}
                    {user?.achievements && user.achievements.length > 0 && (
                      <div
                        style={{
                          background: "#fff",
                          borderRadius: 0,
                          padding: "22px 24px",
                          marginBottom: 12,
                        }}
                      >
                        <h2
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: "#111827",
                            marginBottom: 14,
                          }}
                        >
                          Achievements
                        </h2>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                          }}
                        >
                          {user.achievements.map((achievement, index) => (
                            <div
                              key={index}
                              style={{ display: "flex", gap: 10 }}
                            >
                              <div style={{ flexShrink: 0, marginTop: 2 }}>
                                <div
                                  style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                    background:
                                      "linear-gradient(135deg, #fbbf24, #f97316)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Award size={13} style={{ color: "#fff" }} />
                                </div>
                              </div>
                              <div style={{ flex: 1 }}>
                                {achievement.title && (
                                  <p
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 500,
                                      color: "#111827",
                                      marginBottom: 2,
                                    }}
                                  >
                                    {achievement.title}
                                  </p>
                                )}
                                <p
                                  style={{
                                    fontSize: 14,
                                    color: achievement.title
                                      ? "#6b7280"
                                      : "#111827",
                                    lineHeight: 1.6,
                                  }}
                                >
                                  {achievement.achievement}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ── Column 2: AI Insights ── */}
            <div
              className="flex flex-col overflow-hidden"
              style={{
                marginLeft: "0px",
                background: "#f5f5f5",
                width: "320px",
                flexShrink: 0,
              }}
            >
              <div
                className="shrink-0"
                style={{
                  padding: "16px 16px 12px",
                  marginTop: "8px",
                  background: "#f5f5f5",
                }}
              >
                <div className="flex items-center gap-2">
                  <Sparkles size={12} className="text-zinc-500" />
                  <span
                    className="text-[11px] font-bold uppercase tracking-widest text-zinc-500"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  >
                    AI Insights
                  </span>
                </div>
              </div>
              <div
                className="flex-1 overflow-y-auto hide-scroll"
                style={{ background: "#f5f5f5" }}
              >
                {selectedCandidate.matchingScore ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      padding: "0 8px 8px",
                    }}
                  >
                    {/* Overall score */}
                    <div style={{ background: "#fff", padding: "18px 20px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                        }}
                      >
                        <div>
                          <p
                            style={{
                              fontSize: 11,
                              color: "#a1a1aa",
                              marginBottom: 4,
                              fontFamily: "'IBM Plex Mono', monospace",
                              letterSpacing: "0.06em",
                              textTransform: "uppercase",
                            }}
                          >
                            Overall Match
                          </p>
                          <p
                            style={{
                              fontSize: 12,
                              color: "#71717a",
                              fontFamily: "'IBM Plex Mono', monospace",
                              letterSpacing: "0.05em",
                              textTransform: "uppercase",
                            }}
                          >
                            {selectedCandidate.matchingScore.matchLevel ||
                              "NO_MATCH"}
                          </p>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 2,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 36,
                              fontWeight: 700,
                              color: "#111827",
                              lineHeight: 1,
                              fontFamily: "'IBM Plex Mono', monospace",
                            }}
                          >
                            {selectedCandidate.matchingScore.finalScore || 0}
                          </span>
                          <span
                            style={{
                              fontSize: 14,
                              color: "#a1a1aa",
                              fontFamily: "'IBM Plex Mono', monospace",
                            }}
                          >
                            %
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Requirements */}
                    {selectedCandidate.matchingScore.requirementScores?.length >
                      0 && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {selectedCandidate.matchingScore.requirementScores.map(
                          (req, index) => {
                            const colors = scoreColor(req.score)
                            return (
                              <div
                                key={index}
                                style={{
                                  background: "#fff",
                                  padding: "14px 20px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    gap: 10,
                                    marginBottom: req.evidence ? 6 : 0,
                                  }}
                                >
                                  <p
                                    style={{
                                      fontSize: 12,
                                      color: "#a1a1aa",
                                      lineHeight: 1.5,
                                      fontFamily: "'IBM Plex Sans', sans-serif",
                                      flex: 1,
                                    }}
                                  >
                                    {req.requirement}
                                  </p>
                                  <span
                                    className={`flex-shrink-0 flex items-center justify-center w-8 h-8 text-xs font-bold ${colors.badge}`}
                                    style={{
                                      borderRadius: 0,
                                      fontFamily: "'IBM Plex Mono', monospace",
                                    }}
                                  >
                                    {req.score}
                                  </span>
                                </div>
                                {req.evidence && (
                                  <p
                                    style={{
                                      fontSize: 14,
                                      color: "#18181b",
                                      lineHeight: 1.65,
                                      fontFamily: "'IBM Plex Sans', sans-serif",
                                    }}
                                  >
                                    {req.evidence}
                                  </p>
                                )}
                              </div>
                            )
                          },
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
                    <div
                      className="w-9 h-9 border border-zinc-300 flex items-center justify-center bg-white"
                      style={{ borderRadius: 0 }}
                    >
                      <Sparkles size={14} className="text-zinc-500" />
                    </div>
                    <p
                      className="text-[10px] font-bold uppercase tracking-widest text-zinc-500"
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                      No insights available
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* ── Column 3: Screening Questions ── */}
            <div
              className="flex flex-col overflow-hidden"
              style={{
                marginLeft: "8px",
                marginRight: "16px",
                background: "#f5f5f5",
                width: "320px",
                flexShrink: 0,
              }}
            >
              <div
                className="shrink-0"
                style={{
                  padding: "16px 16px 12px",
                  marginTop: "8px",
                  background: "#f5f5f5",
                }}
              >
                <div className="flex items-center gap-2">
                  <MessageSquare size={12} className="text-zinc-500" />
                  <span
                    className="text-[11px] font-bold uppercase tracking-widest text-zinc-500"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  >
                    Screening Questions
                  </span>
                  {screeningQuestions.length > 0 && (
                    <span
                      className="text-[11px] font-medium text-zinc-400"
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                      · {screeningQuestions.length}
                    </span>
                  )}
                </div>
              </div>
              <div
                className="flex-1 overflow-y-auto hide-scroll"
                style={{ background: "#f5f5f5" }}
              >
                {screeningQuestions.length > 0 ? (
                  <div
                    style={{
                      background: "#fff",
                      padding: "16px 28px 8px",
                      margin: "0 8px",
                    }}
                  >
                    {screeningQuestions.map((item, index) => (
                      <div key={index} style={{ paddingBottom: 20 }}>
                        <p
                          style={{
                            fontSize: 12,
                            color: "#a1a1aa",
                            lineHeight: 1.5,
                            marginBottom: 5,
                            fontFamily: "'IBM Plex Sans', sans-serif",
                          }}
                        >
                          {item.question}
                        </p>
                        <p
                          style={{
                            fontSize: 14,
                            color: "#18181b",
                            lineHeight: 1.65,
                            fontFamily: "'IBM Plex Sans', sans-serif",
                          }}
                        >
                          {item.answer || (
                            <em
                              style={{ color: "#d4d4d8", fontStyle: "italic" }}
                            >
                              No answer provided
                            </em>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
                    <div
                      className="w-9 h-9 border border-zinc-200 flex items-center justify-center bg-white"
                      style={{ borderRadius: 0 }}
                    >
                      <MessageSquare size={14} className="text-zinc-300" />
                    </div>
                    <p
                      className="text-[10px] font-bold uppercase tracking-widest text-zinc-300"
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                      No screening questions
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {showNotesModal && (
          <InternalNotesModal
            notes={internalNotes}
            onSave={handleSaveNotes}
            onClose={() => setShowNotesModal(false)}
            isSaving={isSavingNotes}
          />
        )}
      </div>
    </>
  )
}

export default CandidateDetailsContent